import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum BinNamingField {
  ROW = "ROW",
  RACK = "RACK",
  SHELF = "SHELF",
  BIN = "BIN",
}

export enum SEPARATOR {
  DASH = "-",
}

export enum BinDistributionMode {
  DISTRIBUTE = "DISTRIBUTE",
  DUPLICATE = "DUPLICATE",
}

export const BinFieldUsedSchema = z.record(
  createEnum(BinNamingField, { enumName: "BinNamingField" }),
  z.boolean(),
);

export const BinNamingSettingsSchema = z.object({
  binFieldUsed: BinFieldUsedSchema,
  separator: createEnum(SEPARATOR, { enumName: "BinSeparator" }),
  minDigits: z.number().nullish(),
  binDistributionMode: createEnum(BinDistributionMode, {
    enumName: "BinDistributionMode",
  }).nullish(),
});
export type BinNamingSettings = z.infer<typeof BinNamingSettingsSchema>;

// =============================================================================
// Advanced Template-Based Bin Naming
// =============================================================================

/**
 * Advanced bin naming template.
 *
 * The template string uses a simple pattern language:
 * - {ROW} - replaced with the row name from the rack
 * - {RACK} - replaced with the rack name
 * - {ROW:N} - replaced with row name, padded to N digits if numeric
 * - {RACK:N} - replaced with rack name, padded to N digits if numeric
 * - {start-end} - generates a sequence (numeric or alphabetic)
 *
 * Variable padding:
 * - {RACK:2} - pads numeric rack names to 2 digits (e.g., "1" → "01", "12" → "12")
 * - {ROW:3} - pads numeric row names to 3 digits (e.g., "5" → "005")
 * - Non-numeric values are not padded (e.g., "A" stays "A")
 *
 * Sequence patterns:
 * - {1-50} - numeric sequence 1, 2, 3, ..., 50
 * - {001-050} - zero-padded numeric sequence 001, 002, ..., 050
 * - {A-Z} - alphabetic sequence A, B, C, ..., Z
 * - {50-1} - descending numeric sequence 50, 49, ..., 1
 * - {Z-A} - descending alphabetic sequence Z, Y, ..., A
 *
 * Examples:
 * - "{ROW}-{RACK}-{001-010}-{A-E}" with ROW="A", RACK="1"
 *   → A-1-001-A, A-1-001-B, ..., A-1-010-E
 * - "{ROW}-{RACK:2}-{01-10}" with ROW="A", RACK="5"
 *   → A-05-01, A-05-02, ..., A-05-10
 * - "{RACK}{01-99}" with RACK="R1"
 *   → R101, R102, ..., R199
 */
const AdvancedBinNamingTemplateSchema_ = z.object({
  template: z
    .string()
    .describe("Template string with variables and sequence patterns"),
});
export const AdvancedBinNamingTemplateSchema: z.ZodType<AdvancedBinNamingTemplate> =
  AdvancedBinNamingTemplateSchema_;
export interface AdvancedBinNamingTemplate
  extends z.infer<typeof AdvancedBinNamingTemplateSchema_> {}

/**
 * Team-level advanced bin naming settings.
 */
const AdvancedBinNamingSettingsSchema_ = z.object({
  enabled: z
    .boolean()
    .describe("Whether advanced template-based naming is enabled"),
  defaultTemplate: AdvancedBinNamingTemplateSchema.nullish().describe(
    "Default template used when a rack doesn't specify its own",
  ),
});
export const AdvancedBinNamingSettingsSchema: z.ZodType<AdvancedBinNamingSettings> =
  AdvancedBinNamingSettingsSchema_;
export interface AdvancedBinNamingSettings
  extends z.infer<typeof AdvancedBinNamingSettingsSchema_> {}

// =============================================================================
// Template Parsing and Generation Utilities
// =============================================================================

interface ParsedSegment {
  type: "literal" | "variable" | "sequence";
  value: string;
  values?: string[]; // For sequences, all generated values
  padding?: number; // For variables, minimum digits to pad to
}

/**
 * Parses a sequence pattern like "001-050" or "A-Z" and generates all values.
 */
function parseAndGenerateSequence(pattern: string): string[] | null {
  // Match patterns like "1-50", "001-050", "A-Z", "Z-A"
  const numericMatch = pattern.match(/^(\d+)-(\d+)$/);
  const alphaMatch = pattern.match(/^([A-Za-z])-([A-Za-z])$/);

  if (numericMatch) {
    const [, startStr, endStr] = numericMatch;
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const start = parseInt(startStr, 10);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const end = parseInt(endStr, 10);
    const ascending = start <= end;

    // Only pad if there's an explicit leading zero in the pattern
    // e.g., {01-10} should pad, but {1-10} should not
    const hasLeadingZero =
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (startStr.length > 1 && startStr.startsWith("0")) ||
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      (endStr.length > 1 && endStr.startsWith("0"));
    const digits = hasLeadingZero
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        Math.max(startStr.length, endStr.length)
      : 0;

    const values: string[] = [];
    if (ascending) {
      for (let i = start; i <= end; i++) {
        values.push(
          digits > 0 ? i.toString().padStart(digits, "0") : i.toString(),
        );
      }
    } else {
      for (let i = start; i >= end; i--) {
        values.push(
          digits > 0 ? i.toString().padStart(digits, "0") : i.toString(),
        );
      }
    }
    return values;
  }

  if (alphaMatch) {
    const [, startChar, endChar] = alphaMatch;
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const startCode = startChar.toUpperCase().charCodeAt(0);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const endCode = endChar.toUpperCase().charCodeAt(0);
    const ascending = startCode <= endCode;

    const values: string[] = [];
    if (ascending) {
      for (let i = startCode; i <= endCode; i++) {
        values.push(String.fromCharCode(i));
      }
    } else {
      for (let i = startCode; i >= endCode; i--) {
        values.push(String.fromCharCode(i));
      }
    }
    return values;
  }

  return null;
}

/**
 * Parses a template string into segments.
 * Identifies variables ({ROW}, {RACK}), sequences ({1-50}, {A-Z}), and literals.
 */
function parseTemplate(template: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  const regex = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(template)) !== null) {
    // Add literal segment before this match
    if (match.index > lastIndex) {
      segments.push({
        type: "literal",
        value: template.slice(lastIndex, match.index),
      });
    }

    const content = match[1];

    // Check if it's a variable (ROW or RACK), optionally with padding modifier
    // Supports: {ROW}, {RACK}, {ROW:2}, {RACK:3}
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const variableMatch = content.match(/^(ROW|RACK)(?::(\d+))?$/);
    if (variableMatch) {
      const [, varName, paddingStr] = variableMatch;
      const padding = paddingStr ? parseInt(paddingStr, 10) : undefined;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      segments.push({ type: "variable", value: varName, padding });
    } else {
      // Try to parse as a sequence
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const sequenceValues = parseAndGenerateSequence(content);
      if (sequenceValues) {
        segments.push({
          type: "sequence",
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          value: content,
          values: sequenceValues,
        });
      } else {
        // Unknown pattern, treat as literal
        segments.push({ type: "literal", value: match[0] });
      }
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining literal
  if (lastIndex < template.length) {
    segments.push({ type: "literal", value: template.slice(lastIndex) });
  }

  return segments;
}

/**
 * Applies padding to a variable value.
 * Only pads if the value is purely numeric and shorter than the padding.
 */
function applyVariablePadding(value: string, padding?: number): string {
  if (!padding || padding < 1) {
    return value;
  }
  // Only pad numeric values
  if (/^\d+$/.test(value)) {
    return value.padStart(padding, "0");
  }
  return value;
}

/**
 * Generates all bin names by expanding a template with the given variables.
 *
 * @param template - The advanced bin naming template
 * @param rowName - The row name to substitute for {ROW}
 * @param rackName - The rack name to substitute for {RACK}
 * @returns Array of all generated bin names
 */
export function generateBinNamesFromTemplate(
  template: AdvancedBinNamingTemplate,
  rowName: string,
  rackName: string,
): string[] {
  const segments = parseTemplate(template.template);

  // Find all sequence segments and their values
  const sequenceSegments = segments
    .map((seg, index) => ({ seg, index }))
    .filter(({ seg }) => seg.type === "sequence");

  if (sequenceSegments.length === 0) {
    // No sequences, just substitute variables and return single name
    let result = "";
    for (const seg of segments) {
      if (seg.type === "literal") {
        result += seg.value;
      } else if (seg.type === "variable") {
        const value = seg.value === "ROW" ? rowName : rackName;
        result += applyVariablePadding(value, seg.padding);
      }
    }
    return [result];
  }

  // Generate all combinations of sequences
  const sequenceArrays = sequenceSegments.map(({ seg }) => seg.values!);
  const combinations = cartesianProduct(sequenceArrays);

  const binNames: string[] = [];
  for (const combo of combinations) {
    let result = "";
    let seqIndex = 0;
    for (const seg of segments) {
      switch (seg.type) {
        case "literal": {
          result += seg.value;

          break;
        }
        case "variable": {
          const value = seg.value === "ROW" ? rowName : rackName;
          result += applyVariablePadding(value, seg.padding);

          break;
        }
        case "sequence": {
          result += combo[seqIndex++];

          break;
        }
        // No default
      }
    }
    binNames.push(result);
  }

  return binNames;
}

/**
 * Computes the Cartesian product of multiple arrays.
 */
function cartesianProduct<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [[]];
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (arrays.length === 1) return arrays[0].map((x) => [x]);

  const result: T[][] = [];
  const [first, ...rest] = arrays;
  const restProduct = cartesianProduct(rest);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  for (const item of first) {
    for (const combo of restProduct) {
      result.push([item, ...combo]);
    }
  }

  return result;
}

/**
 * Validates a bin naming template string.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateBinNamingTemplate(template: string): string | null {
  if (!template || template.trim() === "") {
    return "Template cannot be empty";
  }

  const segments = parseTemplate(template);

  // Check for unrecognized patterns (treated as literals but containing braces)
  for (const seg of segments) {
    if (seg.type === "literal" && seg.value.includes("{")) {
      // Extract the pattern that failed to parse
      const match = seg.value.match(/\{([^}]*)\}/);
      if (match) {
        return `Invalid pattern: {${match[1]}}. Use {ROW}, {RACK}, or sequences like {1-10}, {A-Z}`;
      }
    }
  }

  // Check for sequences that would generate too many values
  const sequenceSegments = segments.filter((seg) => seg.type === "sequence");
  if (sequenceSegments.length > 0) {
    let totalCombinations = 1;
    for (const seq of sequenceSegments) {
      totalCombinations *= seq.values?.length ?? 1;
      if (totalCombinations > 10_000) {
        return "Template would generate more than 10,000 bins. Please use smaller ranges.";
      }
    }
  }

  // Check for empty sequences
  for (const seq of sequenceSegments) {
    if (!seq.values || seq.values.length === 0) {
      return `Invalid sequence: {${seq.value}}`;
    }
  }

  return null;
}

/**
 * Returns the count of bins that would be generated from a template.
 */
export function countBinsFromTemplate(template: string): number {
  const segments = parseTemplate(template);
  const sequenceSegments = segments.filter((seg) => seg.type === "sequence");

  if (sequenceSegments.length === 0) {
    return 1;
  }

  let count = 1;
  for (const seq of sequenceSegments) {
    count *= seq.values?.length ?? 1;
  }
  return count;
}

/**
 * Returns the count of each sequence in a template.
 * Used to calculate which "level" (first sequence index) a bin belongs to.
 *
 * For example, template "{ROW}-{RACK}-{1-5}-{A-D}" returns [5, 4]
 * (5 values in first sequence, 4 in second)
 */
export function getSequenceCounts(template: string): number[] {
  const segments = parseTemplate(template);
  const sequenceSegments = segments.filter((seg) => seg.type === "sequence");
  return sequenceSegments.map((seg) => seg.values?.length ?? 1);
}

/**
 * Calculates the first sequence index from a flat bin index.
 * This is used to apply storage location type ranges to the "level" dimension
 * of advanced templates.
 *
 * For a template with sequences [5, 4] (20 total bins):
 * - Flat index 0-3 → first sequence index 0
 * - Flat index 4-7 → first sequence index 1
 * - Flat index 8-11 → first sequence index 2
 * - etc.
 */
export function getFirstSequenceIndex(
  flatIndex: number,
  sequenceCounts: number[],
): number {
  if (sequenceCounts.length === 0) {
    return 0;
  }

  // Product of all sequences after the first
  const divisor = sequenceCounts
    .slice(1)
    .reduce((acc, count) => acc * count, 1);
  return Math.floor(flatIndex / divisor);
}
